import React, { useState, useEffect } from 'react';
import { Tabs, Table, Switch, Button, message, Spin, Alert, Space, Typography, Tooltip, Tag } from 'antd';
import { 
  EyeOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CheckOutlined, 
  ExportOutlined,
  SaveOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const PerfilPermissoesEditor = ({ perfilId, perfilNome }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissoes, setPermissoes] = useState({});
  const [recursos, setRecursos] = useState([]);
  const [acoes, setAcoes] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (perfilId) {
      carregarDados();
    }
  }, [perfilId]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      // Carregar recursos, ações e matriz de permissões
      const [recursosRes, acoesRes, matrizRes] = await Promise.all([
        api.get('/api/permissoes/recursos'),
        api.get('/api/permissoes/acoes'),
        api.get(`/api/permissoes/perfil/${perfilId}/matriz`)
      ]);

      setRecursos(recursosRes.data);
      setAcoes(acoesRes.data);
      
      // Converter matriz em estrutura editável
      const permissoesData = {};
      Object.entries(matrizRes.data.modulos).forEach(([modulo, recursos]) => {
        recursos.forEach(rec => {
          permissoesData[rec.recurso_id] = {
            pode_visualizar: rec.pode_visualizar,
            pode_criar: rec.pode_criar,
            pode_editar: rec.pode_editar,
            pode_excluir: rec.pode_excluir,
            pode_validar: rec.pode_validar,
            pode_exportar: rec.pode_exportar,
            apenas_proprios: rec.apenas_proprios,
            apenas_unidade: rec.apenas_unidade
          };
        });
      });
      
      setPermissoes(permissoesData);
      setHasChanges(false);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      message.error('Erro ao carregar permissões');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (recursoId, campo, valor) => {
    setPermissoes(prev => ({
      ...prev,
      [recursoId]: {
        ...prev[recursoId],
        [campo]: valor
      }
    }));
    setHasChanges(true);
  };

  const handleSalvar = async () => {
    setSaving(true);
    try {
      // Preparar dados para envio
      const batch = Object.entries(permissoes).map(([recursoId, perms]) => ({
        recurso_id: parseInt(recursoId),
        ...perms
      }));

      await api.put(`/api/permissoes/perfil/${perfilId}/batch`, batch);

      message.success('Permissões salvas com sucesso!');
      setHasChanges(false);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      message.error('Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  const getIconeAcao = (nomeAcao) => {
    const icones = {
      visualizar: <EyeOutlined />,
      criar: <PlusOutlined />,
      editar: <EditOutlined />,
      excluir: <DeleteOutlined />,
      validar: <CheckOutlined />,
      exportar: <ExportOutlined />
    };
    return icones[nomeAcao] || null;
  };

  const renderTabela = (modulo) => {
    const recursosDoModulo = recursos.filter(r => r.modulo === modulo);

    const columns = [
      {
        title: 'Recurso',
        dataIndex: 'descricao',
        key: 'descricao',
        width: 250,
        fixed: 'left',
        render: (text, record) => (
          <Space direction="vertical" size={0}>
            <Text strong>{text}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.nome}
            </Text>
          </Space>
        )
      },
      {
        title: <Tooltip title="Visualizar"><EyeOutlined /> Ver</Tooltip>,
        key: 'visualizar',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <Switch
            checked={permissoes[record.id]?.pode_visualizar || false}
            onChange={(checked) => handleToggle(record.id, 'pode_visualizar', checked)}
            size="small"
          />
        )
      },
      {
        title: <Tooltip title="Criar"><PlusOutlined /> Criar</Tooltip>,
        key: 'criar',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <Switch
            checked={permissoes[record.id]?.pode_criar || false}
            onChange={(checked) => handleToggle(record.id, 'pode_criar', checked)}
            size="small"
          />
        )
      },
      {
        title: <Tooltip title="Editar"><EditOutlined /> Editar</Tooltip>,
        key: 'editar',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <Switch
            checked={permissoes[record.id]?.pode_editar || false}
            onChange={(checked) => handleToggle(record.id, 'pode_editar', checked)}
            size="small"
          />
        )
      },
      {
        title: <Tooltip title="Excluir"><DeleteOutlined /> Excluir</Tooltip>,
        key: 'excluir',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <Switch
            checked={permissoes[record.id]?.pode_excluir || false}
            onChange={(checked) => handleToggle(record.id, 'pode_excluir', checked)}
            size="small"
          />
        )
      },
      {
        title: <Tooltip title="Validar"><CheckOutlined /> Validar</Tooltip>,
        key: 'validar',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <Switch
            checked={permissoes[record.id]?.pode_validar || false}
            onChange={(checked) => handleToggle(record.id, 'pode_validar', checked)}
            size="small"
          />
        )
      },
      {
        title: <Tooltip title="Exportar"><ExportOutlined /> Exportar</Tooltip>,
        key: 'exportar',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <Switch
            checked={permissoes[record.id]?.pode_exportar || false}
            onChange={(checked) => handleToggle(record.id, 'pode_exportar', checked)}
            size="small"
          />
        )
      },
      {
        title: 'Restrições',
        key: 'restricoes',
        width: 200,
        render: (_, record) => (
          <Space direction="vertical" size={2}>
            <Tooltip title="Usuário pode acessar apenas seus próprios registros">
              <Space>
                <Switch
                  checked={permissoes[record.id]?.apenas_proprios || false}
                  onChange={(checked) => handleToggle(record.id, 'apenas_proprios', checked)}
                  size="small"
                />
                <Text style={{ fontSize: 11 }}>Apenas próprios</Text>
              </Space>
            </Tooltip>
            <Tooltip title="Usuário pode acessar apenas registros da sua unidade">
              <Space>
                <Switch
                  checked={permissoes[record.id]?.apenas_unidade || false}
                  onChange={(checked) => handleToggle(record.id, 'apenas_unidade', checked)}
                  size="small"
                />
                <Text style={{ fontSize: 11 }}>Apenas unidade</Text>
              </Space>
            </Tooltip>
          </Space>
        )
      }
    ];

    return (
      <Table
        dataSource={recursosDoModulo}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="small"
        scroll={{ x: 1000 }}
        bordered
      />
    );
  };

  const modulosUnicos = [...new Set(recursos.map(r => r.modulo))].sort();

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="Carregando permissões..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Cabeçalho */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Permissões do Perfil: {perfilNome}
            </Title>
            <Text type="secondary">
              Configure as permissões de acesso para cada recurso do sistema
            </Text>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={carregarDados}
              disabled={hasChanges}
            >
              Recarregar
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSalvar}
              loading={saving}
              disabled={!hasChanges}
            >
              Salvar Alterações
            </Button>
          </Space>
        </div>

        {/* Alerta de mudanças não salvas */}
        {hasChanges && (
          <Alert
            message="Você tem alterações não salvas"
            description="Lembre-se de clicar em 'Salvar Alterações' para aplicar as modificações."
            type="warning"
            showIcon
            closable
          />
        )}

        {/* Legenda */}
        <Alert
          message={
            <Space size="large" wrap>
              <Tag color="blue">Ver - Visualizar registros</Tag>
              <Tag color="green">Criar - Adicionar novos registros</Tag>
              <Tag color="orange">Editar - Modificar registros</Tag>
              <Tag color="red">Excluir - Remover registros</Tag>
              <Tag color="purple">Validar - Aprovar/validar registros</Tag>
              <Tag color="cyan">Exportar - Exportar dados</Tag>
            </Space>
          }
          type="info"
        />

        {/* Tabelas por módulo */}
        <Tabs defaultActiveKey={modulosUnicos[0]}>
          {modulosUnicos.map(modulo => (
            <TabPane tab={modulo.charAt(0).toUpperCase() + modulo.slice(1)} key={modulo}>
              {renderTabela(modulo)}
            </TabPane>
          ))}
        </Tabs>
      </Space>
    </div>
  );
};

export default PerfilPermissoesEditor;
